/**
 * 公共分页组件 — 列表页通用分页 UI
 *
 * 功能特性：
 * - 显示上一页/下一页按钮和页码信息
 * - 可选显示总记录数
 * - 可选在只有一页时隐藏整个组件
 * - 按钮禁用边界处理（第一页禁用上一页，最后一页禁用下一页）
 * - 页码越界自动钳制到有效范围
 *
 * 主要导出：`CrmPagination`（自定义元素 `<crm-pagination>`）、`PageChangeEvent` 类型
 *
 * 用法：
 *   <crm-pagination
 *     .current=${this.currentPage}
 *     .perPage=${this.perPage}
 *     .total=${this.total}
 *     @page-change=${(e: PageChangeEvent) => { this.currentPage = e.detail.page; this.loadData(); }}
 *   ></crm-pagination>
 */
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';

/** 页码变化事件类型 */
export type PageChangeEvent = CustomEvent<{ page: number }>;

@customElement('crm-pagination')
export class CrmPagination extends BaseElement {
  /** 当前页码（从 1 开始） */
  @property({ type: Number }) current = 1;
  /** 每页条数，默认 20 */
  @property({ type: Number }) perPage = 20;
  /** 总记录数 */
  @property({ type: Number }) total = 0;
  /** 是否显示总记录数，默认 true */
  @property({ type: Boolean }) showTotal = true;
  /** 仅一页时是否隐藏整个分页组件，默认 true */
  @property({ type: Boolean }) hideOnSinglePage = true;

  /** 计算总页数，至少为 1 */
  private get totalPages(): number {
    if (this.total <= 0 || this.perPage <= 0) return 1;
    return Math.max(1, Math.ceil(this.total / this.perPage));
  }

  /**
   * 派发页码变化事件。
   * 将目标页码钳制到 [1, totalPages] 范围内，若与当前页相同则不派发。
   * @param page - 目标页码
   * @sideeffect 派发 `page-change` 自定义事件
   */
  private emitChange(page: number) {
    const target = Math.min(Math.max(1, page), this.totalPages);
    if (target === this.current) return;
    this.dispatchEvent(
      new CustomEvent('page-change', {
        detail: { page: target },
        bubbles: true,
        composed: false,
      }) satisfies PageChangeEvent
    );
  }

  /**
   * 渲染分页组件。
   * 当 hideOnSinglePage 为 true 且总页数 ≤ 1 时返回空模板。
   * @returns Lit 模板或空字符串
   */
  render() {
    const totalPages = this.totalPages;
    if (this.hideOnSinglePage && totalPages <= 1) return '';

    return html`
      <nav
        class="flex justify-center items-center gap-2 py-4 text-text-secondary text-sm border-t border-border flex-shrink-0"
        aria-label="分页"
      >
        <button
          class="px-3 py-1.5 bg-surface hover:bg-bg border border-border text-text rounded disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm transition-colors"
          aria-label="上一页"
          ?disabled=${this.current <= 1}
          @click=${() => this.emitChange(this.current - 1)}
        >上一页</button>
        <span>第 ${this.current} / ${totalPages} 页</span>
        ${this.showTotal ? html`<span>共 ${this.total} 条</span>` : ''}
        <button
          class="px-3 py-1.5 bg-surface hover:bg-bg border border-border text-text rounded disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm transition-colors"
          aria-label="下一页"
          ?disabled=${this.current >= totalPages}
          @click=${() => this.emitChange(this.current + 1)}
        >下一页</button>
      </nav>
    `;
  }
}
